"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import { calcBathroom, type BathroomResult, type BathType, type BathTier, type BathShower, BATH_NAR_JOY_SCORE, BATH_NAR_ROI_PCT } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const BATH_TYPE_OPTIONS = [
  { value: "half",   label: "Half Bath (powder room, no tub/shower)" },
  { value: "full",   label: "Full Bathroom" },
  { value: "master", label: "Master / Primary Bathroom" },
];
const TIER_OPTIONS = [
  { value: "basic",    label: "Cosmetic Update — paint, fixtures, accessories" },
  { value: "midrange", label: "Full Remodel — new tile, vanity, tub/shower" },
  { value: "luxury",   label: "Luxury — custom tile, heated floors, smart fixtures" },
];
const SHOWER_OPTIONS = [
  { value: "existing", label: "Keep existing tub/shower" },
  { value: "shower",   label: "Add or replace walk-in shower (+$3,000)" },
  { value: "tub",      label: "Add or replace soaking tub (+$2,000)" },
  { value: "both",     label: "Separate tub + walk-in shower (+$4,500)" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [sqft, setSqft] = useState("");
  const [bathType, setBathType] = useState<BathType>("full");
  const [tier, setTier] = useState<BathTier>("midrange");
  const [movePlumbing, setMovePlumbing] = useState(false);
  const [showerTub, setShowerTub] = useState<BathShower>("existing");
  const [result, setResult] = useState<BathroomResult | null>(null);

  function handleCalculate() {
    setResult(calcBathroom({ sqft: parseFloat(sqft) || 0, bathType, tier, movePlumbing, showerTub }));
  }

  const spec = CALCULATOR_SPECS.bathroom;

  return (
    <CalculatorShell
      name="Bathroom Remodel Cost Calculator"
      description="Get a realistic bathroom renovation budget from a quick cosmetic refresh to a luxury master bath with a Joy Score of 9.8/10."
      icon="bathroom"
      category="high-value"
      accentColor="#a78bfa"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Bathroom labor costs vary significantly by region. Plumbing and tile work are the biggest cost drivers. Always obtain 3+ licensed contractor quotes."
      inputsPanel={
        <div className="space-y-4">
          <SelectField id="bath-type" label="Bathroom Type" value={bathType} onChange={(e) => { setBathType(e.target.value as BathType); setResult(null); }} options={BATH_TYPE_OPTIONS} hint="Minimum costs apply: Half $3K · Full $8K · Master $15K" />
          <InputField id="bath-sqft" label="Bathroom Size" unit="sq ft" type="number" min="0" step="5" placeholder="50" value={sqft} onChange={(e) => { setSqft(e.target.value); setResult(null); }} hint="Typical: half bath ~35 sq ft · full bath ~50 sq ft · master ~100+ sq ft" />
          <SelectField id="bath-tier" label="Remodel Scope" value={tier} onChange={(e) => { setTier(e.target.value as BathTier); setResult(null); }} options={TIER_OPTIONS} />
          <SelectField id="shower-tub" label="Tub / Shower Work" value={showerTub} onChange={(e) => { setShowerTub(e.target.value as BathShower); setResult(null); }} options={SHOWER_OPTIONS} />
          <CheckboxField id="move-plumbing" label="Moving plumbing lines" hint="Moving toilet, sink, or shower drain adds ~$3,500" checked={movePlumbing} onChange={(v) => { setMovePlumbing(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Estimated Total Cost Range</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>base {fmt(result.baseEstimate)} · ±20% regional variance</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="NAR Joy Score" value={`${BATH_NAR_JOY_SCORE}/10`} accent sub="Homeowner satisfaction (NAR 2025)" />
              <ResultCard label="Resale ROI" value={`~${BATH_NAR_ROI_PCT}%`} highlight sub="Cost recovered at sale" />
            </div>
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Cost Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: `Base remodel (${tier})`, value: fmt(result.baseEstimate) },
                  ...(result.showerTubCost > 0 ? [{ label: "Tub / shower work", value: fmt(result.showerTubCost) }] : []),
                  ...(result.plumbingCost > 0 ? [{ label: "Plumbing relocation", value: fmt(result.plumbingCost) }] : []),
                  { label: "Midpoint estimate", value: fmt(result.totalMid), bold: true },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                      row.bold
                        ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                        : "border-l-3 border-l-transparent"
                    }`}
                  >
                    <span className={`text-[12.5px] font-sans ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                      {row.label}
                    </span>
                    <span className={`font-mono text-[13px] ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Fixtures, Finishes &amp; Cost Benchmarks</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            The national median for a full <Link href="/articles/bathroom-remodel-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">bathroom renovation</Link> is <strong>{spec.buyingGuide.avgBathRemodel2026.value}</strong> according to the NKBA 2026 Cost & Trends Report, but the range is enormous — from $3,000 for a simple half-bath powder room refresh to $75,000+ for a luxury master bath with heated tile floors, a freestanding tub, and custom vanity. The single biggest cost driver is labor, particularly tile work, which can run $15–$25 per square foot installed. Choosing a prefabricated shower surround instead of custom tile can cut those costs in half. EPA WaterSense-certified fixtures add minimal upfront cost yet <strong>{spec.buyingGuide.waterSenseFixtures.value}</strong>, with many utilities offering rebate programs.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/bathroom_buying_guide.webp" alt="Renovated luxury master bathroom with freestanding soaking tub, walk-in shower, and heated tile floors" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/bathroom_site_prep.webp" alt="Bathroom under renovation showing cement tile backer installation and waterproof membrane application in shower area" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Ventilation, GFCI &amp; Code Requirements</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Bathroom renovations trigger several mandatory code requirements. Under ASHRAE 62.2 and IRC Section R303.3, bathrooms must have mechanical ventilation of at least <strong>{spec.sitePrep.ventilationRequired.value}</strong> — and that exhaust fan must vent to the exterior, not into the attic. Under NEC 210.8(A)(1), <strong>{spec.sitePrep.gfciRequired.value}</strong> must have GFCI protection. These are not optional upgrades — they are legally required any time electrical work is performed in a bathroom. Structural layouts must also respect minimum plumbing clearances: the IRC requires <strong>{spec.industryStandards.minimumClearances.value}</strong>, with ADA-compliant layouts requiring even more space.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.minimumClearances.standardCode}
        sourceName={spec.industryStandards.minimumClearances.sourceName}
        sourceUrl={spec.industryStandards.minimumClearances.sourceUrl}
        paragraphs={[
          <p key="p1">
            Under <b>{spec.industryStandards.minimumClearances.standardCode}</b>, a toilet must have at least <b>15 inches of clearance from its centerline to any side wall, partition, or obstruction</b>, and a minimum 21-inch clearance in front. These dimensions define the minimum code-compliant bathroom footprint — a half bath can theoretically be as small as 36 × 66 inches, though most designers recommend at least 40 × 72 inches for comfortable use.
          </p>,
          <p key="p2">
            For ADA-compliant bathrooms — increasingly relevant for aging-in-place renovations — the requirements are more demanding: 18 inches from toilet centerline to the near side wall, 60 inches of clear floor space for a wheelchair turning radius, and grab bar blocking embedded in the wall framing. Planning grab bar backing during any bathroom renovation is strongly recommended, as retrofitting it after tile installation requires demolition. Blocking costs just $100–$200 to add during rough framing but protects long-term accessibility and <Link href="/articles/renovations-add-resale-value/" className="text-[var(--accent)] hover:underline font-semibold">resale value</Link>. Additionally, verify your water heater's capacity; you can check the average <Link href="/articles/how-long-does-a-water-heater-last/" className="text-[var(--accent)] hover:underline font-semibold">water heater lifespan</Link> if you need to upgrade for a multi-jet shower system.
          </p>
        ]}
        keyRequirements={[
          "Toilet centerline must be at least 15 inches from any side wall, partition, or obstruction.",
          "A minimum clearance of 21 inches is required in front of the toilet.",
          "ADA-compliant layouts require 18 inches from toilet centerline to the near side wall.",
          "ADA-compliant bathrooms require 60 inches of clear floor space for wheelchair turning."
        ]}
      />

      <FaqSection calculatorId="bathroom" />
      <RelatedCalculators calculatorId="bathroom" />
    </CalculatorShell>
  );
}
